/**
 * The second-factor screens a sign-in swaps to (module-private to `<LoginForm>`): the N152 enrolment
 * hand-off, the first-factor enrolment (Q-L16-98 · F-98-16 / Q-ARG-1), and the MFA verification —
 * whose refused code is shown from the held refusal (W16), translated and carrying its terminal tone.
 *
 * A function, not a component: it answers the screen to render, or `null` when the sign-in is still on
 * its credential step.
 *
 * @module @puffin-core/login-shell/components/loginForm/secondFactorStep
 */

import { MfaVerification } from '@puffin-core/mfa-ui/verification';
import { MfaEnrollmentHandoff } from '../MfaEnrollmentHandoff.jsx';
import { PasskeySetupHandoff } from '../PasskeySetupHandoff.jsx';
import { HeldFactorStep } from '../HeldFactorStep.jsx';

/**
 * The in-place step a raised hold calls for, through the ONE `<HeldFactorStep>` (SECREQ S6c (d)). WHETHER the hold is
 * answered in place is the form's (`form.passkeyEnrollment` / `form.pinEnrollment` / `form.mfaEnrollmentRequired`);
 * each factor's finish is the form's own sign-in tail for it.
 *
 * @param {Object} options - as `renderSecondFactorStep`'s
 * @returns {React.JSX.Element}
 */
function heldFactorStep({ form, auth, appName, t }) {
  const tails = {
    mfa: form.handleMfaEnrollmentFinish,
    passkey: form.passkeyEnrollment?.onFinish,
    pin: form.pinEnrollment?.onFinish,
  };
  return (
    <HeldFactorStep
      auth={auth}
      appName={appName}
      loading={form.isLoading}
      error={form.error}
      pinEnrollmentStep={form.pinEnrollment?.Step ?? null}
      onFinish={(factor) => tails[factor]()}
      onCancel={auth.cancelMfaEnrollment}
      t={t}
    />
  );
}

/**
 * @param {Object} options
 * @param {Object} options.form - `useLoginForm`'s state.
 * @param {Object} options.auth - The surface's auth contract.
 * @param {string} [options.appName] - The brand lockup the enrolment screen names.
 * @param {Object} [options.redirectConfig]
 * @param {(key: string) => string} options.translate - The `login`-namespace translator.
 * @param {Function} [options.t] - The consumer translator.
 * @returns {?React.JSX.Element}
 */
export function renderSecondFactorStep({ form, auth, appName, redirectConfig, translate, t }) {
  // Q-L16-98 · F-98-16 / Q-ARG-1 — a platform staff member with no second factor, or a tenant person whose MFA an
  // administrator's reset cleared, enrols here through the sign-in's enrolment token, and is signed in when the backup
  // codes are acknowledged.
  // N152 — the marketing apex holds the same person, and hands them to their company's sign-in page to enrol there.
  // SECREQ S6d (owner ruling Q5): a person held for a passkey in an app that sets none up is handed to the apps that do.
  if (form.passkeySetupHandoff) {
    return (
      <PasskeySetupHandoff
        appKey={form.passkeySetupHandoff.appKey}
        loginUrlFor={form.passkeySetupHandoff.loginUrlFor}
        onBack={form.passkeySetupHandoff.onBack}
        t={t}
      />
    );
  }

  // SECREQ S6b: a person held for a passkey in an app that sets one up creates it here; S6c (c): a person held for a PIN
  // sets it here, through the HOST's step (whatever leg raised the hold, D7). Each is signed in when it is set up (or
  // held again for the factor still owed). SECREQ S6c (d): both render through the ONE `<HeldFactorStep>`.
  if (form.passkeyEnrollment || form.pinEnrollment) {
    return heldFactorStep({ form, auth, appName, t });
  }

  if (form.mfaEnrollmentHandoff && redirectConfig?.buildTenantLoginUrl) {
    return (
      <MfaEnrollmentHandoff
        tenantLoginUrl={redirectConfig.buildTenantLoginUrl(form.mfaEnrollmentHandoff.subdomain)}
        onBack={form.handleMfaCancel}
        t={t}
      />
    );
  }

  // After the N152 handoff, never before it: the apex hands the person to their company's page instead.
  if (form.mfaEnrollmentRequired) {
    return heldFactorStep({ form, auth, appName, t });
  }

  if (form.mfaRequired) {
    // W16: the held refusal, in the reader's language, and its terminal tone as its own flag (MfaVerification takes
    // words only and refuses an Error, THINKPAD's ruling 2026-10-28); else the auth context's own error.
    const mfaError = form.mfaRefusal ? translate(form.mfaRefusal.messageKey) : form.error;
    const mfaErrorTerminal = form.mfaRefusal ? form.mfaRefusal.terminal === true : false;
    return (
      <MfaVerification
        methods={form.mfaMethods}
        preferredMethod={form.preferredMethod}
        maskedPhone={form.maskedPhone}
        maskedEmail={form.maskedEmail}
        onVerify={form.handleMfaVerify}
        onVerifyPasskey={form.handleMfaPasskeyVerify}
        onCancel={form.handleMfaCancel}
        isLoading={form.isLoading}
        error={mfaError}
        errorTerminal={mfaErrorTerminal}
        t={t}
      />
    );
  }

  return null;
}
